import React, { FC, useRef } from 'react';
import { Field, WrappedFieldArrayProps } from 'redux-form';
import { ConfigurationGroupOptionType } from 'shared-types/SpaPackage';
import { OptionSelector } from '@common/components/OptionSelector';
import { useTranslations } from '@lang';
import { InfoBox } from '@common/components';
import { GroupError } from '../GroupError';

import { ConfigurationGroupHeading } from './ConfigurationGroupHeading';
import { ConfigurationGroupWrapper } from './ConfigurationGroupHeading.styles';
import { validateTimeSlot } from './validators';

type ConfigurationGroupProps = {
  options: ConfigurationGroupOptionType[];
  minimumSelections?: number;
  maximumSelections?: number;
  supportsSelections?: boolean;
  requireTimeSlots: boolean;
  configurationGroupId: number;
  index: number;
  supportsTimeslots?: boolean;
} & WrappedFieldArrayProps;

export const ConfigurationGroup: FC<ConfigurationGroupProps> = ({
  options,
  minimumSelections,
  maximumSelections,
  supportsSelections,
  requireTimeSlots,
  fields,
  meta,
  configurationGroupId,
  index,
  supportsTimeslots,
}) => {
  const { t } = useTranslations();
  const timeLabel = t('common.time').toLowerCase();
  const validate = useRef(
    validateTimeSlot(timeLabel, requireTimeSlots, supportsSelections, supportsTimeslots)
  );

  if (!fields.length) {
    options.forEach(({ id, timeslots }) => {
      if (!supportsSelections && !timeslots) {
        return;
      }

      fields.push({
        configurationGroupId,
        optionId: id,
        isSelected: false,
        timeslot: null,
      });
    });
  }

  const numberSelectedOptions = fields.getAll()?.filter((v) => v.isSelected).length;
  return (
    <ConfigurationGroupWrapper>
      <ConfigurationGroupHeading
        minimumSelections={minimumSelections}
        maximumSelections={maximumSelections}
        numberSelected={numberSelectedOptions}
        className={index > 0 ? 'with-top-margin' : ''}
      />
      {requireTimeSlots && <InfoBox>{t('spa.timeslotsWarning')}</InfoBox>}
      <GroupError meta={meta} />
      {fields.map((fieldName, index) => {
        const { isSelected, optionId } = fields.get(index);
        const { name, timeslots } = options.find((o) => o.id === optionId) || {};

        let disabled = maximumSelections
          ? numberSelectedOptions >= maximumSelections && !isSelected
          : false;

        if (timeslots?.length === 0) {
          disabled = true;
        }

        return (
          <Field
            key={optionId}
            name={fieldName}
            component={OptionSelector}
            optionTitle={name}
            supportsSelections={supportsSelections}
            disabled={disabled}
            timeslots={timeslots}
            requireTimeSlots={requireTimeSlots}
            configurationGroupId={configurationGroupId}
            validate={validate.current}
          />
        );
      })}
    </ConfigurationGroupWrapper>
  );
};
